// The top row of the dashboard: the question an agent is waiting on, a device waiting to be
// approved, and where credentials live. The question's text is end-to-end encrypted, so the
// dashboard names the run and sends you to it to read and answer on this device.
import { ArrowRight, CheckCircle, DeviceMobileCamera, LockKey, Question, ShieldCheck } from "@phosphor-icons/react";
import { Link } from "react-router";

import type { Device } from "../api/client";
import { ago } from "../pages/format";
import { cardClass, IconTile } from "../ui/Card";
import { cn } from "../ui/cn";
import { E2EEBadge } from "../ui/status";
import type { PendingQuestion } from "./api";
import { useT } from "../i18n";

export function QuestionNotice({ questions, className }: { questions: PendingQuestion[]; className?: string }) {
  const t = useT().dashboard;
  const [first, ...rest] = [...questions].sort((a, b) => a.asked_at - b.asked_at);
  if (!first) {
    return (
      <section className={cn(cardClass, "flex items-center gap-4 p-5", className)} aria-label={t.questions}>
        <IconTile className="bg-green-lighter text-green-dark">
          <CheckCircle weight="fill" />
        </IconTile>
        <div>
          <div className="font-display text-[15px] font-semibold text-gray-900">{t.noneWaiting}</div>
          <div className="text-[13px] text-gray-500">{t.noneWaitingBody}</div>
        </div>
      </section>
    );
  }
  return (
    <section
      aria-label={t.questions}
      data-testid="dashboard-question"
      className={cn(
        "relative overflow-hidden rounded-xl border border-orange/40 bg-gradient-to-br from-orange-lighter/70 via-gray-0 to-gray-0 p-5 dark:from-orange-lighter/50 dark:via-gray-50 dark:to-gray-50",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange text-white dark:text-gray-0">
          <Question weight="fill" className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 basis-[calc(100%-44px)] sm:basis-0">
          <div className="text-[13px] font-semibold text-gray-900">
            <Link to={`/runs/${first.run_id}`} className="text-gray-900 hover:text-primary">
              {t.needsAnswer(first.workflow_name ?? t.aRun)}
            </Link>
          </div>
          <div className="text-xs text-gray-500">{t.asked(ago(first.asked_at))}</div>
        </div>
        <E2EEBadge />
      </div>
      <p className="mb-0 mt-4 flex items-start gap-2 font-display text-[15px] font-semibold leading-snug text-gray-900">
        <LockKey weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
        {t.questionEncrypted}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <Link to={`/runs/${first.run_id}`} className="inline-flex items-center gap-1 text-[13px] font-medium text-primary">
          {t.openConversation} <ArrowRight weight="bold" className="h-3.5 w-3.5" />
        </Link>
        {rest.length > 0 ? <span className="text-[13px] text-gray-500">{t.moreWaiting(rest.length)}</span> : null}
      </div>
      <p className="mb-0 mt-3 text-[11.5px] text-gray-500">
        {t.answerNote}
      </p>
    </section>
  );
}

export function PendingDeviceNotice({ device }: { device: Device }) {
  const t = useT().dashboard;
  return (
    <div className={cn(cardClass, "flex items-center gap-3 p-4")} role="group" aria-label={t.deviceWaiting}>
      <IconTile className="bg-primary-lighter text-primary">
        <DeviceMobileCamera weight="duotone" />
      </IconTile>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-snug text-gray-900">{t.wantsToLink(device.name)}</div>
        <div className="truncate text-[11.5px] text-gray-500">{t.checkFingerprint}</div>
      </div>
      <Link to="/devices" className="rounded-md border border-gray-300 px-3 py-1.5 text-[13px] font-medium text-gray-900 hover:border-gray-1000">
        {t.review}
      </Link>
    </div>
  );
}

export function CredentialsNote({ calls, denied }: { calls: number | undefined; denied: number }) {
  const t = useT().dashboard;
  return (
    <div className={cn(cardClass, "flex flex-1 items-start gap-3 p-4")}>
      <IconTile className="bg-secondary-lighter text-secondary-dark">
        <ShieldCheck weight="duotone" />
      </IconTile>
      <div className="text-[13px] leading-relaxed text-gray-600">
        <span className="font-semibold text-gray-900">{t.credentialsStay}</span>{" "}
        {calls === undefined ? t.brokerActs : t.callsMade(calls, denied)}
      </div>
    </div>
  );
}
